/* Fabric Swatches Sampler — native fallback + secure lifetime-limit service + EmailJS customer confirmation */
(() => {
  'use strict';

  /* =========================================================
     EMAILJS SETTINGS (owner + customer emails)
     Ye 4 values EmailJS account se copy kar ke yahan paste karein.
     Jab tak ye khali hain, form bilkul pehle jaisa kaam karega
     (sirf Shopify wali email jayegi).
     ========================================================= */
  const EMAILJS_CONFIG = {
    publicKey: '2y8Xga7kq3E5H9DZg',          // EmailJS > Account > Public Key
    serviceId: 'service_5m0fp2y',            // EmailJS > Email Services > Service ID
    ownerTemplateId: 'template_ykt9208',     // EmailJS > Email Templates > Owner template ID
    customerTemplateId: 'template_qf8i45d'   // EmailJS > Email Templates > Customer template ID
  };

  const EMAILJS_ENDPOINT = 'https://api.emailjs.com/api/v1.0/email/send';
  const EMAILJS_TIMEOUT_MS = 20000; /* SMTP thora slow ho sakta hai */
  const EMAILJS_GAP_MS = 1200; /* EmailJS free plan: 1 email per second */

  const SELECTOR = '[data-fabric-sampler]';

  class FabricSampler {
    constructor(root) {
      this.root = root;
      this.cards = Array.from(root.querySelectorAll('[data-swatch-card]'));
      this.slots = Array.from(root.querySelectorAll('[data-selected-slot]'));
      this.form = root.querySelector('form.fabric-order-form');
      this.formContent = root.querySelector('[data-swatch-form-content]');
      this.successBox = root.querySelector('[data-swatch-form-success]');
      this.successHeading = root.querySelector('[data-swatch-success-heading]');
      this.successMessage = root.querySelector('[data-swatch-success-message]');
      this.submitButton = root.querySelector('[data-swatch-submit]');
      this.submitLabel = root.querySelector('[data-submit-label]');
      this.counter = root.querySelector('[data-selected-count]');
      this.message = root.querySelector('[data-selection-message]');
      this.countField = root.querySelector('[data-swatch-count-field]');
      this.bodyField = root.querySelector('[data-swatch-body-field]');
      this.emailInput = root.querySelector('[data-swatch-email]');
      this.emailStatus = root.querySelector('[data-email-status]');
      this.honeypot = root.querySelector('[data-swatch-honeypot]');
      this.phoneField = root.querySelector('[data-phone-field]');
      this.phoneLocal = root.querySelector('[data-phone-local]');
      this.phonePrefixElement = root.querySelector('[data-phone-prefix]');
      this.toast = root.querySelector('[data-swatch-toast]');
      this.toastTitle = root.querySelector('[data-toast-title]');
      this.toastMessage = root.querySelector('[data-toast-message]');

      this.maxSelections = this.toPositiveInteger(root.dataset.maxSelections, 6);
      this.minSelections = Math.min(
        this.toPositiveInteger(root.dataset.minSelections, 1),
        this.maxSelections
      );
      this.rememberSelection = root.dataset.rememberSelection === 'true';
      this.emptyMessage = root.dataset.emptyMessage || 'Select at least 1 swatch to continue.';
      this.limitMessage = root.dataset.limitMessage || `You can select a maximum of ${this.maxSelections} swatches.`;
      this.loadingText = root.dataset.loadingText || 'Submitting…';
      this.defaultButtonText = this.submitLabel ? this.submitLabel.textContent.trim() : 'Place order';

      this.submissionMode = root.dataset.submissionMode === 'secure_api' ? 'secure_api' : 'native_contact';
      this.apiEndpoint = String(root.dataset.apiEndpoint || '/apps/fabric-swatches').replace(/\/+$/, '');
      this.checkEmailOnBlur = root.dataset.checkEmail === 'true';
      this.apiErrorMessage = root.dataset.apiErrorMessage || 'We could not submit your request right now. Please try again.';
      this.emailCheckingMessage = root.dataset.emailCheckingMessage || 'Checking your free swatch allowance…';
      this.emailRemainingMessage = root.dataset.emailRemainingMessage || 'You have [remaining] free swatch(es) remaining.';
      this.lifetimeZeroMessage = root.dataset.lifetimeZeroMessage || 'This email address has already received the maximum allowance of 6 free fabric swatches.';
      this.limitPopupTitle = root.dataset.limitPopupTitle || 'Maximum swatches selected';
      this.limitPopupMessage = root.dataset.limitPopupMessage || this.limitMessage;
      this.lifetimePopupTitle = root.dataset.lifetimePopupTitle || 'Free swatch allowance reached';
      this.toastDuration = this.toPositiveInteger(root.dataset.toastDuration, 3500);
      this.formSuccessHeading = root.dataset.formSuccessHeading || 'Thank you — your swatch request has been received.';
      this.formSuccessMessage = root.dataset.formSuccessMessage || 'A confirmation email has been sent to you.';

      this.selected = [];
      this.isSubmitting = false;
      this.storageKey = `fabric-sampler:${root.dataset.sectionId || 'default'}`;
      this.progressCleanups = [];
      this.allowanceKnown = false;
      this.allowanceUsed = 0;
      this.allowanceRemaining = this.maxSelections;
      this.lastCheckedEmail = '';
      this.emailCheckController = null;
      this.toastTimer = null;
      this.requestId = this.createRequestId();

      /* EmailJS state */
      this.customerEmailSent = false;
      this.bypassNativeIntercept = false;

      this.nativePostedSuccess = Boolean(
        this.successBox &&
        !this.successBox.classList.contains('is-hidden') &&
        this.formContent &&
        this.formContent.hasAttribute('hidden')
      );
      this.apiPostedSuccess = false;

      this.onRootClick = this.handleRootClick.bind(this);
      this.onFormSubmit = this.handleFormSubmit.bind(this);
      this.onEmailBlur = this.handleEmailBlur.bind(this);
      this.onEmailInput = this.handleEmailInput.bind(this);

      root.addEventListener('click', this.onRootClick);
      /* Window capture: Shopify ke apne submit scripts se pehle chalta hai */
      if (this.form) window.addEventListener('submit', this.onFormSubmit, true);
      if (this.emailInput) {
        this.emailInput.addEventListener('blur', this.onEmailBlur);
        this.emailInput.addEventListener('input', this.onEmailInput);
      }

      if (this.nativePostedSuccess) {
        this.clearRememberedSelection();
      } else {
        this.restoreSelection();
      }

      this.initialiseSliderProgress();
      this.render();
    }

    toPositiveInteger(value, fallback) {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }

    createRequestId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      return `swatch-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    }

    getSwatchData(card) {
      return {
        id: card.dataset.swatchId || card.dataset.swatchHandle,
        handle: card.dataset.swatchHandle || '',
        name: card.dataset.swatchName || 'Fabric swatch',
        category: card.dataset.swatchCategory || '',
        image: card.dataset.swatchImage || ''
      };
    }

    isSuccessState() {
      return this.nativePostedSuccess || this.apiPostedSuccess;
    }

    getEffectiveLimit() {
      if (this.submissionMode === 'secure_api' && this.allowanceKnown) {
        return Math.max(0, Math.min(this.maxSelections, this.allowanceRemaining));
      }
      return this.maxSelections;
    }

    handleRootClick(event) {
      const card = event.target.closest('[data-swatch-card]');
      if (card && this.root.contains(card)) {
        this.toggleCard(card);
        return;
      }

      const filledSlot = event.target.closest('[data-selected-slot].is-filled');
      if (filledSlot && this.root.contains(filledSlot)) {
        const index = Number.parseInt(filledSlot.dataset.slotIndex, 10);
        if (Number.isFinite(index)) this.removeSelectionByIndex(index);
      }
    }

    toggleCard(card) {
      if (this.isSubmitting || this.isSuccessState()) return;

      const swatch = this.getSwatchData(card);
      const existingIndex = this.selected.findIndex((item) => item.id === swatch.id);

      if (existingIndex !== -1) {
        this.selected.splice(existingIndex, 1);
        this.persistSelection();
        this.render();
        return;
      }

      const effectiveLimit = this.getEffectiveLimit();
      if (this.selected.length >= effectiveLimit) {
        this.showLimitPopup();
        this.pulseSelectionPanel();
        return;
      }

      this.selected.push(swatch);
      this.persistSelection();
      this.render();
    }

    removeSelectionByIndex(index) {
      if (this.isSubmitting || this.isSuccessState() || index < 0 || index >= this.selected.length) return;
      this.selected.splice(index, 1);
      this.persistSelection();
      this.render();
    }

    showLimitPopup() {
      if (
        this.submissionMode === 'secure_api' &&
        this.allowanceKnown &&
        this.allowanceRemaining < this.maxSelections
      ) {
        const message = this.allowanceRemaining <= 0
          ? this.lifetimeZeroMessage
          : this.emailRemainingMessage.replace('[remaining]', String(this.allowanceRemaining));
        this.showToast(this.lifetimePopupTitle, message);
        return;
      }
      this.showToast(this.limitPopupTitle, this.limitPopupMessage);
    }

    render() {
      const selectedIds = new Set(this.selected.map((item) => item.id));
      const effectiveLimit = this.getEffectiveLimit();
      const limitReached = this.selected.length >= effectiveLimit;
      const overAllowance = this.selected.length > effectiveLimit;

      this.cards.forEach((card) => {
        const isSelected = selectedIds.has(card.dataset.swatchId || card.dataset.swatchHandle);
        const blocked = limitReached && !isSelected;
        card.classList.toggle('is-selected', isSelected);
        card.classList.toggle('is-limit-blocked', blocked);
        card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        card.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      });

      this.slots.forEach((slot, index) => {
        const swatch = this.selected[index];
        slot.innerHTML = '';
        slot.classList.toggle('is-empty', !swatch);
        slot.classList.toggle('is-filled', Boolean(swatch));
        slot.disabled = !swatch;

        if (!swatch) {
          slot.setAttribute('aria-label', `Empty swatch selection ${index + 1}`);
          const plus = document.createElement('span');
          plus.className = 'fabric-selection__plus';
          plus.setAttribute('aria-hidden', 'true');
          plus.textContent = '+';
          slot.appendChild(plus);
          return;
        }

        slot.setAttribute('aria-label', `Remove ${swatch.name} from selected swatches`);

        const image = document.createElement('img');
        image.src = swatch.image;
        image.alt = swatch.name;
        image.loading = 'lazy';

        const name = document.createElement('span');
        name.className = 'fabric-selection__slot-name';
        name.textContent = swatch.name;

        const remove = document.createElement('span');
        remove.className = 'fabric-selection__remove';
        remove.setAttribute('aria-hidden', 'true');
        remove.textContent = '×';

        slot.append(image, name, remove);
      });

      if (this.counter) this.counter.textContent = String(this.selected.length);

      this.updateSubmissionFields();

      const canSubmit =
        !this.isSuccessState() &&
        this.selected.length >= this.minSelections &&
        !overAllowance &&
        effectiveLimit > 0 &&
        !this.isSubmitting;

      if (this.submitButton) this.submitButton.disabled = !canSubmit;

      if (this.isSuccessState()) {
        this.setMessage('', false);
      } else if (this.selected.length < this.minSelections) {
        this.setMessage(this.emptyMessage, false);
      } else if (overAllowance) {
        const message = effectiveLimit <= 0
          ? this.lifetimeZeroMessage
          : this.emailRemainingMessage.replace('[remaining]', String(effectiveLimit));
        this.setMessage(message, true);
      } else {
        this.setMessage('', false);
      }
    }

    getSelectedLines() {
      return this.selected.map((swatch, index) => {
        const suffix = swatch.category ? ` — ${swatch.category}` : '';
        return `${index + 1}. ${swatch.name}${suffix}`;
      });
    }

    updateSubmissionFields() {
      const lines = this.getSelectedLines();

      if (this.countField) this.countField.value = String(this.selected.length);

      if (this.phoneField) {
        const local = this.phoneLocal ? this.phoneLocal.value.trim() : '';
        const prefix = this.phonePrefixElement ? this.phonePrefixElement.dataset.phonePrefix || '' : '';
        this.phoneField.value = local
          ? (local.startsWith('+') ? local : `${prefix} ${local}`.trim())
          : '';
      }

      if (this.bodyField) {
        const summary = lines.length ? lines.join('\n') : 'No swatches selected';
        this.bodyField.value = [
          'Free Fabric Swatch Request',
          '',
          'Selected swatches:',
          summary,
          '',
          `Total selected: ${this.selected.length} of ${this.maxSelections}`
        ].join('\n');
      }
    }

    handleEmailInput() {
      const email = this.normaliseEmail(this.emailInput ? this.emailInput.value : '');
      if (email !== this.lastCheckedEmail) {
        this.allowanceKnown = false;
        this.allowanceUsed = 0;
        this.allowanceRemaining = this.maxSelections;
        this.setEmailStatus('', '');
        this.render();
      }
    }

    handleEmailBlur() {
      if (this.submissionMode !== 'secure_api' || !this.checkEmailOnBlur) return;
      const email = this.normaliseEmail(this.emailInput ? this.emailInput.value : '');
      if (!this.isValidEmail(email)) return;
      this.checkAllowance(email, false);
    }

    normaliseEmail(value) {
      return String(value || '').trim().toLowerCase();
    }

    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    apiUrl(path) {
      return `${this.apiEndpoint}${path}`;
    }

    async checkAllowance(email, force) {
      if (this.submissionMode !== 'secure_api') return true;
      if (!force && this.allowanceKnown && email === this.lastCheckedEmail) return true;

      if (this.emailCheckController) this.emailCheckController.abort();
      this.emailCheckController = new AbortController();
      this.setEmailStatus(this.emailCheckingMessage, '');

      try {
        const response = await fetch(this.apiUrl('/check'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ email }),
          signal: this.emailCheckController.signal
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || this.apiErrorMessage);

        this.allowanceKnown = true;
        this.allowanceUsed = Number.isFinite(Number(data.used)) ? Number(data.used) : 0;
        this.allowanceRemaining = Math.max(0, Math.min(this.maxSelections, Number(data.remaining)));
        this.lastCheckedEmail = email;

        if (this.allowanceRemaining <= 0) {
          this.setEmailStatus(this.lifetimeZeroMessage, 'error');
          this.showToast(this.lifetimePopupTitle, this.lifetimeZeroMessage);
        } else {
          this.setEmailStatus(
            this.emailRemainingMessage.replace('[remaining]', String(this.allowanceRemaining)),
            'ok'
          );
        }

        this.render();
        return true;
      } catch (error) {
        if (error && error.name === 'AbortError') return false;
        this.allowanceKnown = false;
        this.setEmailStatus(error.message || this.apiErrorMessage, 'error');
        this.render();
        return false;
      }
    }

    getFormValue(name) {
      if (!this.form || !this.form.elements) return '';
      const field = this.form.elements.namedItem(name);
      return field && typeof field.value === 'string' ? field.value.trim() : '';
    }

    buildApiPayload() {
      return {
        requestId: this.requestId,
        firstName: this.getFormValue('contact[first_name]'),
        lastName: this.getFormValue('contact[last_name]'),
        email: this.normaliseEmail(this.getFormValue('contact[email]')),
        phone: this.phoneField ? this.phoneField.value.trim() : '',
        address1: this.getFormValue('contact[address]'),
        address2: this.getFormValue('contact[address_line_2]'),
        postcode: this.getFormValue('contact[postcode]'),
        city: this.getFormValue('contact[city]'),
        honeypot: this.honeypot ? this.honeypot.value : '',
        swatches: this.selected.map((swatch) => ({
          id: swatch.id,
          name: swatch.name,
          category: swatch.category
        }))
      };
    }

    /* ---------- EmailJS: customer confirmation ---------- */

    emailjsReady() {
      return Boolean(
        EMAILJS_CONFIG.publicKey &&
        EMAILJS_CONFIG.serviceId &&
        EMAILJS_CONFIG.ownerTemplateId &&
        typeof window.fetch === 'function'
      );
    }

    buildEmailParams() {
      const firstName = this.getFormValue('contact[first_name]');
      const lastName = this.getFormValue('contact[last_name]');
      const email = this.getFormValue('contact[email]');
      const phone = this.phoneField ? this.phoneField.value.trim() : '';
      const address = [
        this.getFormValue('contact[address]'),
        this.getFormValue('contact[address_line_2]'),
        this.getFormValue('contact[city]'),
        this.getFormValue('contact[postcode]')
      ].filter(Boolean).join(', ');
      const lines = this.getSelectedLines();
      const customerName = [firstName, lastName].filter(Boolean).join(' ');

      return {
        first_name: firstName,
        last_name: lastName,
        customer_name: customerName,
        customer_email: email,
        to_email: email,
        reply_to: email,
        phone,
        address,
        selected_fabrics: lines.join('\n'),
        swatch_count: String(this.selected.length),
        message: [
          'Free Fabric Swatch Request',
          '',
          `Customer: ${customerName}`,
          `Email: ${email}`,
          `Phone: ${phone}`,
          `Address: ${address}`,
          '',
          'Selected swatches:',
          lines.join('\n'),
          '',
          `Total selected: ${this.selected.length}`
        ].join('\n')
      };
    }

    async sendEmailJSOnce(templateId, params) {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = window.setTimeout(() => {
        if (controller) controller.abort();
      }, EMAILJS_TIMEOUT_MS);

      try {
        const response = await fetch(EMAILJS_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            service_id: EMAILJS_CONFIG.serviceId,
            template_id: templateId,
            user_id: EMAILJS_CONFIG.publicKey,
            template_params: params
          }),
          signal: controller ? controller.signal : undefined
        });
        return response.ok ? 'ok' : 'error';
      } catch (error) {
        /* Timeout: email shayad chali gayi ho, is liye dobara nahi bhejte */
        if (error && error.name === 'AbortError') return 'timeout';
        return 'error';
      } finally {
        window.clearTimeout(timer);
      }
    }

    wait(ms) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    }

    /* Ek dafa fail ho to thora ruk kar dobara try karo */
    async sendWithRetry(templateId, params) {
      if (!templateId) return false;
      try {
        const first = await this.sendEmailJSOnce(templateId, params);
        if (first !== 'error') return true;
        await this.wait(1500);
        const second = await this.sendEmailJSOnce(templateId, params);
        return second !== 'error';
      } catch (error) {
        return false;
      }
    }

    /* Shopify contact form ko normal tareeqe se submit karo (owner email Shopify se jayegi) */
    submitNativeForm() {
      if (!this.form) return;
      this.bypassNativeIntercept = true;
      try {
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit();
        } else {
          HTMLFormElement.prototype.submit.call(this.form);
        }
      } finally {
        this.bypassNativeIntercept = false;
      }
    }

    async handleFormSubmit(event) {
      /* Sirf isi section ka form */
      if (!this.form || event.target !== this.form) return;

      /* EmailJS fail hone par backup Shopify submit — isay mat roko */
      if (this.bypassNativeIntercept) {
        return;
      }

      if (this.isSuccessState()) {
        event.preventDefault();
        return;
      }

      if (this.selected.length < this.minSelections) {
        event.preventDefault();
        this.setMessage(this.emptyMessage, true);
        this.showToast(this.limitPopupTitle, this.emptyMessage);
        this.pulseSelectionPanel();
        return;
      }

      this.updateSubmissionFields();

      if (this.form && typeof this.form.checkValidity === 'function' && !this.form.checkValidity()) {
        event.preventDefault();
        if (typeof this.form.reportValidity === 'function') this.form.reportValidity();
        return;
      }

      if (this.submissionMode !== 'secure_api') {
        const isBot = Boolean(this.honeypot && this.honeypot.value);

        /* EmailJS ka kaam chal raha ho to doosra submit (Shopify ka) roko */
        if (this.emailjsReady() && this.customerEmailSent && !isBot) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        /* EmailJS set nahi, ya bot: normal Shopify submit */
        if (!this.emailjsReady() || isBot) {
          this.setSubmitting(true);
          return;
        }

        /* EmailJS se owner + customer ko bhejo. Shopify ko submit nahi (duplicate band) */
        event.preventDefault();
        event.stopImmediatePropagation();
        this.customerEmailSent = true;
        this.setSubmitting(true);

        const params = this.buildEmailParams();
        const ownerOk = await this.sendWithRetry(EMAILJS_CONFIG.ownerTemplateId, params);

        if (!ownerOk) {
          /* Backup: EmailJS fail ho to Shopify se submit (owner ko Shopify email jayegi) */
          this.submitNativeForm();
          return;
        }

        if (EMAILJS_CONFIG.customerTemplateId && params.to_email) {
          await this.wait(EMAILJS_GAP_MS);
          await this.sendWithRetry(EMAILJS_CONFIG.customerTemplateId, params);
        }

        this.apiPostedSuccess = true;
        this.clearRememberedSelection();
        this.showApiSuccess({});
        this.render();
        if (this.successBox && typeof this.successBox.scrollIntoView === 'function') {
          this.successBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      event.preventDefault();

      const email = this.normaliseEmail(this.emailInput ? this.emailInput.value : '');
      const checked = await this.checkAllowance(email, true);
      if (!checked) return;

      const effectiveLimit = this.getEffectiveLimit();
      if (effectiveLimit <= 0 || this.selected.length > effectiveLimit) {
        this.showLimitPopup();
        this.render();
        return;
      }

      this.setSubmitting(true);

      try {
        const response = await fetch(this.apiUrl('/submit'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(this.buildApiPayload())
        });

        const data = await response.json().catch(() => ({}));

        if (response.status === 409 && data.code === 'LIFETIME_LIMIT') {
          this.allowanceKnown = true;
          this.allowanceUsed = Number(data.used || 0);
          this.allowanceRemaining = Math.max(0, Number(data.remaining || 0));
          this.lastCheckedEmail = email;
          const message = data.message || (
            this.allowanceRemaining <= 0
              ? this.lifetimeZeroMessage
              : this.emailRemainingMessage.replace('[remaining]', String(this.allowanceRemaining))
          );
          this.setEmailStatus(message, 'error');
          this.showToast(this.lifetimePopupTitle, message);
          this.setSubmitting(false);
          this.render();
          return;
        }

        if (!response.ok) throw new Error(data.message || this.apiErrorMessage);

        this.apiPostedSuccess = true;
        this.allowanceKnown = true;
        this.allowanceUsed = Number(data.used || this.selected.length);
        this.allowanceRemaining = Math.max(0, Number(data.remaining || 0));
        this.clearRememberedSelection();
        this.showApiSuccess(data);
        this.render();
      } catch (error) {
        this.setSubmitting(false);
        const message = error.message || this.apiErrorMessage;
        this.setMessage(message, true);
        this.showToast('Unable to submit', message);
      }
    }

    showApiSuccess(data) {
      if (this.formContent) this.formContent.setAttribute('hidden', 'hidden');
      if (this.successBox) this.successBox.classList.remove('is-hidden');
      if (this.successHeading) {
        this.successHeading.textContent = data.successHeading || this.formSuccessHeading;
      }
      if (this.successMessage) {
        this.successMessage.textContent = data.successMessage || this.formSuccessMessage;
      }
      this.selected = [];
      if (this.form && typeof this.form.reset === 'function') this.form.reset();
      this.setSubmitting(false);
    }

    setEmailStatus(text, type) {
      if (!this.emailStatus) return;
      this.emailStatus.textContent = text || '';
      this.emailStatus.classList.toggle('is-ok', type === 'ok');
      this.emailStatus.classList.toggle('is-error', type === 'error');
    }

    showToast(title, message) {
      if (!this.toast) return;
      if (this.toastTitle) this.toastTitle.textContent = title || '';
      if (this.toastMessage) this.toastMessage.textContent = message || '';
      this.toast.classList.add('is-visible');
      this.toast.setAttribute('aria-hidden', 'false');
      window.clearTimeout(this.toastTimer);
      this.toastTimer = window.setTimeout(() => {
        this.toast.classList.remove('is-visible');
        this.toast.setAttribute('aria-hidden', 'true');
      }, this.toastDuration);
    }

    setMessage(text, isError) {
      if (!this.message) return;
      this.message.textContent = text || '';
      this.message.classList.toggle('is-error', Boolean(isError));
    }

    pulseSelectionPanel() {
      const panel = this.root.querySelector('.fabric-selection');
      if (!panel || typeof panel.animate !== 'function') return;
      panel.animate(
        [
          { transform: 'translateX(0)' },
          { transform: 'translateX(-4px)' },
          { transform: 'translateX(4px)' },
          { transform: 'translateX(0)' }
        ],
        { duration: 220, easing: 'ease-out' }
      );
    }

    persistSelection() {
      if (!this.rememberSelection) return;
      try {
        window.localStorage.setItem(
          this.storageKey,
          JSON.stringify(this.selected.map((item) => item.id))
        );
      } catch (error) {
        // Selection still works if storage is unavailable.
      }
    }

    restoreSelection() {
      if (!this.rememberSelection) return;
      try {
        const stored = JSON.parse(window.localStorage.getItem(this.storageKey) || '[]');
        if (!Array.isArray(stored)) return;
        const restored = [];
        stored.slice(0, this.maxSelections).forEach((id) => {
          const card = this.cards.find(
            (item) => (item.dataset.swatchId || item.dataset.swatchHandle) === id
          );
          if (card) restored.push(this.getSwatchData(card));
        });
        this.selected = restored;
      } catch (error) {
        this.selected = [];
      }
    }

    clearRememberedSelection() {
      this.selected = [];
      if (!this.rememberSelection) return;
      try {
        window.localStorage.removeItem(this.storageKey);
      } catch (error) {
        // Ignore storage errors after successful submission.
      }
    }

    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;
      this.root.classList.toggle('is-submitting', isSubmitting);

      if (this.submitButton) {
        this.submitButton.classList.toggle('is-loading', isSubmitting);
      }
      if (this.submitLabel) {
        this.submitLabel.textContent = isSubmitting ? this.loadingText : this.defaultButtonText;
      }
      this.render();
    }

    initialiseSliderProgress() {
      this.progressCleanups.forEach((cleanup) => cleanup());
      this.progressCleanups = [];

      this.root.querySelectorAll('[data-fabric-category]').forEach((category) => {
        const track = category.querySelector('[data-swatch-track]');
        const fill = category.querySelector('[data-slider-progress-fill]');
        if (!track || !fill) return;

        let frame = null;
        const update = () => {
          if (frame) window.cancelAnimationFrame(frame);
          frame = window.requestAnimationFrame(() => {
            const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
            const percent = maxScroll <= 0
              ? 100
              : Math.max(6, Math.min(100, (track.scrollLeft / maxScroll) * 100));
            fill.style.width = `${percent}%`;
          });
        };

        track.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        update();

        this.progressCleanups.push(() => {
          track.removeEventListener('scroll', update);
          window.removeEventListener('resize', update);
          if (frame) window.cancelAnimationFrame(frame);
        });
      });
    }

    destroy() {
      this.root.removeEventListener('click', this.onRootClick);
      if (this.form) window.removeEventListener('submit', this.onFormSubmit, true);
      if (this.emailInput) {
        this.emailInput.removeEventListener('blur', this.onEmailBlur);
        this.emailInput.removeEventListener('input', this.onEmailInput);
      }
      if (this.emailCheckController) this.emailCheckController.abort();
      window.clearTimeout(this.toastTimer);
      this.progressCleanups.forEach((cleanup) => cleanup());
      this.progressCleanups = [];
      delete this.root.__fabricSamplerInstance;
    }
  }

  function initialise(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.__fabricSamplerInstance) return;
      root.__fabricSamplerInstance = new FabricSampler(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.__fabricSamplerInstance) root.__fabricSamplerInstance.destroy();
    });
  });
})();